import { cva } from 'class-variance-authority'
import { ArrowUp01Icon, Tick02Icon } from '@hugeicons/react'
import clsx from 'clsx'
import { useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import { GameIcon } from '@/shared/ui/GameIcon'
import { computeCheckpointLayout } from '@/shared/ui/progressBarCheckpointLayout'

export type ProgressBarVariant =
  | 'primary'
  | 'progress'
  | 'success'
  | 'locked'

export type ProgressBarSize = 'sm' | 'md' | 'lg'

export type ProgressBarProps = {
  value: number
  variant?: ProgressBarVariant
  size?: ProgressBarSize
  /** When undefined, glow applies if value > 75 and variant is not locked */
  glow?: boolean
  className?: string
  'aria-label'?: string
}

const trackVariants = cva(
  [
    'w-full overflow-hidden rounded',
    'bg-[#ececf2]',
    'shadow-[inset_0_2px_4px_rgba(0,0,0,0.06)]',
  ],
  {
    variants: {
      size: {
        sm: 'h-1.5',
        md: 'h-2',
        lg: 'h-2.5',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
)

const fillVariants = cva(
  [
    'relative h-full min-w-0 rounded-[inherit]',
    'transition-[width] duration-[600ms] ease-[cubic-bezier(0.22,1,0.36,1)]',
    'before:pointer-events-none before:absolute before:inset-0 before:rounded-[inherit]',
    'before:bg-gradient-to-b before:from-white/35 before:to-transparent',
  ],
  {
    variants: {
      variant: {
        primary:
          'bg-gradient-to-r from-[color-mix(in_oklab,var(--color-brand)_86%,white)] to-[var(--color-brand)]',
        progress: 'bg-gradient-to-r from-[#ffb74d] to-status-progress',
        success: 'bg-gradient-to-r from-[#4ade80] to-status-success',
        locked: 'bg-[#d1d5db]',
      },
      glow: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      {
        variant: 'primary',
        glow: true,
        class: 'shadow-[0_0_12px_color-mix(in_oklab,var(--color-brand)_35%,transparent)]',
      },
      {
        variant: 'progress',
        glow: true,
        class: 'shadow-[0_0_12px_rgba(255,152,0,0.35)]',
      },
      {
        variant: 'success',
        glow: true,
        class: 'shadow-[0_0_12px_rgba(22,163,74,0.35)]',
      },
    ],
    defaultVariants: {
      variant: 'primary',
      glow: false,
    },
  }
)

/**
 * Barra de progresso gamificada — trilho inset com sombra interna, fill em
 * gradiente com shine sutil no topo. `glow` acende automaticamente quando
 * `value > 75` (e `variant !== 'locked'`): reforço emocional de "quase lá",
 * não decoração — pode ser forçado via prop. Variante = mesma semântica de
 * cor do Badge/Button (`primary` roxo, `progress` laranja, `success` verde,
 * `locked` cinza sem glow).
 *
 * @example
 * <ProgressBar value={45} variant="progress" />
 * <ProgressBar value={82} variant="primary" size="lg" />
 */
export const ProgressBar = ({
  value,
  variant = 'primary',
  size = 'md',
  glow,
  className,
  'aria-label': ariaLabel
}: ProgressBarProps) => {
  const clamped = Math.min(100, Math.max(0, value))
  const showGlow =
    glow ?? (clamped > 75 && variant !== 'locked')

  return (
    <div
      className={clsx('w-full', className)}
      role="progressbar"
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={ariaLabel}
    >
      <div className={trackVariants({ size })}>
        <div
          className={fillVariants({ variant, glow: showGlow })}
          style={{ width: `${clamped}%` }}
        />
      </div>
    </div>
  )
}

ProgressBar.displayName = 'ProgressBar'

export type ProgressBarRowProps = {
  value: number
  /** Slot esquerdo da linha de cima — texto/label livre, ex.: "0 de 1 fases concluídas". */
  label: ReactNode
  /** Conteúdo extra na célula esquerda — ícone (CardHint), recompensas, etc. */
  slot?: ReactNode
  variant?: ProgressBarVariant
  size?: ProgressBarSize
  glow?: boolean
  className?: string
  'aria-label'?: string
}

/**
 * Modo canônico "resumo": linha de cima em 2 colunas (label à esquerda,
 * percentual à direita), barra sozinha na linha de baixo, ocupando a largura
 * inteira. Usar sempre que a barra precisa de um rótulo — não compor
 * `ProgressBar` + texto solto ao lado, que é o padrão que este substitui.
 *
 * @example
 * <ProgressBar.Row value={62} label="0 de 1 fases concluídas" />
 */
const Row = ({
  value,
  label,
  slot,
  variant,
  size,
  glow,
  className,
  'aria-label': ariaLabel
}: ProgressBarRowProps) => {
  const clamped = Math.min(100, Math.max(0, value))

  return (
    <div className={clsx('flex flex-col gap-1.5', className)}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1">
          {slot}
          <span className="min-w-0 text-2xs font-semibold text-gray-600">
            {label}
          </span>
        </div>
        <span className="shrink-0 text-sm font-bold text-gray-800">
          {clamped}%
        </span>
      </div>
      <ProgressBar
        value={clamped}
        variant={variant}
        size={size}
        glow={glow}
        aria-label={ariaLabel}
      />
    </div>
  )
}
Row.displayName = 'ProgressBar.Row'

ProgressBar.Row = Row

export type ProgressBarSegmentsProps = {
  /** Quantidade de marcos — modo segmentado legado, sem checkpoints contínuos. */
  total: number
  /** Marcos já conquistados — modo segmentado legado. */
  achieved: number
  /** Avanço dentro do marco corrente, de 0 a 1 — modo segmentado legado. */
  currentFill?: number
  /** Contagem atual na meta (ex.: 7 parceiros). */
  progressValue?: number
  /** Alvo total da meta (ex.: 75 parceiros). */
  max?: number
  /** Quantidades de cada marco — posiciona checkpoints na barra contínua. */
  checkpointTargets?: number[]
  /** Checkpoints sobre a trilha — check verde nos marcos conquistados. */
  showMarkers?: boolean
  /** Recompensa flutuante no próximo marco alcançável (ex.: +10). */
  checkpointReward?: number | null
  variant?: ProgressBarVariant
  size?: ProgressBarSize
  className?: string
  /** Texto anunciado por leitor de tela, ex.: "7 de 75 objetivos". */
  'aria-valuetext'?: string
}

const segmentHeights: Record<ProgressBarSize, string> = {
  sm: 'h-1.5',
  md: 'h-2',
  lg: 'h-2.5'
}

/** Faixa vertical dos checkpoints — maior que a trilha; círculos ficam em cima dela. */
const checkpointLaneHeights: Record<ProgressBarSize, string> = {
  sm: 'h-3.5',
  md: 'h-4',
  lg: 'h-5'
}

const checkpointRewardOffsets: Record<ProgressBarSize, string> = {
  sm: 'mb-5',
  md: 'mb-6',
  lg: 'mb-7'
}

const markerSizes: Record<ProgressBarSize, string> = {
  sm: 'size-3',
  md: 'size-3.5',
  lg: 'size-4'
}

const markerIconSizes: Record<ProgressBarSize, number> = {
  sm: 8,
  md: 9,
  lg: 10
}

const markerSizePx: Record<ProgressBarSize, number> = {
  sm: 12,
  md: 14,
  lg: 16
}

const minCenterGapPx: Record<ProgressBarSize, number> = {
  sm: 20,
  md: 22,
  lg: 24
}

const segmentFills: Record<ProgressBarVariant, string> = {
  primary:
    'bg-gradient-to-r from-[color-mix(in_oklab,var(--color-brand)_86%,white)] to-[var(--color-brand)]',
  progress: 'bg-gradient-to-r from-[#ffb74d] to-status-progress',
  success: 'bg-gradient-to-r from-[#4ade80] to-status-success',
  locked: 'bg-[#d1d5db]'
}

const lastAchievedCheckpointTarget = (value: number, targets: number[]): number =>
  targets.reduce((last, target) => (value >= target ? target : last), 0)

type SegmentState = 'achieved' | 'current' | 'pending'

const markerStates: Record<
  'current' | 'pending',
  Record<ProgressBarVariant, string>
> = {
  current: {
    primary:
      'border-[var(--color-brand)] bg-[color-mix(in_oklab,var(--color-brand)_35%,white)]',
    progress: 'border-status-progress bg-[#fff4de]',
    success: 'border-status-success bg-[#dcfce7]',
    locked: 'border-gray-400 bg-gray-200'
  },
  pending: {
    primary: 'border-gray-300 bg-white',
    progress: 'border-gray-300 bg-white',
    success: 'border-gray-300 bg-white',
    locked: 'border-gray-300 bg-gray-100'
  }
}

const SegmentCheckpoint = ({
  state,
  variant,
  size
}: {
  state: SegmentState
  variant: ProgressBarVariant
  size: ProgressBarSize
}) => {
  const halo = 'shadow-[0_0_0_2px_#fff]'

  if (state === 'achieved') {
    return (
      <span
        data-checkpoint="achieved"
        className={clsx(
          'flex shrink-0 items-center justify-center rounded-full bg-status-success',
          halo,
          markerSizes[size]
        )}
      >
        <Tick02Icon
          size={markerIconSizes[size]}
          variant="solid"
          className="text-white"
          aria-hidden
        />
      </span>
    )
  }

  return (
    <span
      data-checkpoint={state}
      className={clsx(
        'box-border shrink-0 rounded-full border-2',
        halo,
        markerSizes[size],
        markerStates[state][variant]
      )}
    />
  )
}

const CheckpointRewardFloater = ({ value }: { value: number }) => (
  <div data-checkpoint-reward className="relative inline-block leading-none">
    <ArrowUp01Icon
      size={8}
      variant="solid"
      className="absolute bottom-full left-1/2 -translate-x-1/2 translate-y-[6px] text-status-progress"
      aria-hidden
    />
    <div
      className={clsx(
        'inline-flex items-center gap-0.5 rounded border',
        'border-[var(--color-reward-pill-coins-border,#edd9b8)]',
        'bg-[var(--color-reward-pill-coins-surface,#fff8f0)]',
        'px-1 py-px text-[var(--color-metric-coins)] shadow-sm',
        'whitespace-nowrap'
      )}
    >
      <GameIcon name="coins" size={9} className="shrink-0" aria-hidden />
      <span className="text-[10px] font-bold leading-none tabular-nums">+{value}</span>
    </div>
  </div>
)

const resolveCheckpointTargets = (
  checkpointTargets: number[],
  total: number,
  max: number
): number[] => {
  if (checkpointTargets.length > 0) return checkpointTargets
  if (total <= 0 || max <= 0) return []

  return Array.from({ length: total }, (_, index) =>
    Math.round((max / total) * (index + 1))
  )
}

type ContinuousCheckpointBarProps = {
  value: number
  max: number
  targets: number[]
  checkpointStates: SegmentState[]
  firstPendingIndex: number
  checkpointReward: number | null | undefined
  showCheckpointReward: boolean
  variant: ProgressBarVariant
  size: ProgressBarSize
  className?: string
  ariaValueText?: string
  showFullSuccess: boolean
  achievedPercent: number
  progressPercent: number
}

const ContinuousCheckpointBar = ({
  value,
  max,
  targets,
  checkpointStates,
  firstPendingIndex,
  checkpointReward,
  showCheckpointReward,
  variant,
  size,
  className,
  ariaValueText,
  showFullSuccess,
  achievedPercent,
  progressPercent
}: ContinuousCheckpointBarProps) => {
  const trackRef = useRef<HTMLDivElement>(null)
  const [trackWidth, setTrackWidth] = useState(0)

  useLayoutEffect(() => {
    const element = trackRef.current
    if (!element) return undefined

    const updateWidth = () => setTrackWidth(element.clientWidth)

    updateWidth()

    if (typeof ResizeObserver === 'undefined') return undefined

    const observer = new ResizeObserver(updateWidth)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  const focusIndex =
    firstPendingIndex >= 0 ? firstPendingIndex : targets.length - 1

  const layout = useMemo(
    () =>
      computeCheckpointLayout({
        targets,
        max,
        width: trackWidth,
        focusIndex,
        markerSizePx: markerSizePx[size],
        minCenterGapPx: minCenterGapPx[size]
      }),
    [targets, max, trackWidth, focusIndex, size]
  )

  const rewardIndex =
    showCheckpointReward && firstPendingIndex >= 0 ? firstPendingIndex : -1

  const rewardViewportLeft =
    rewardIndex >= 0 && layout.positions.length > 0
      ? layout.positions[rewardIndex] + layout.translateX
      : rewardIndex >= 0
        ? (targets[rewardIndex] / max) * (trackWidth > 0 ? trackWidth : 100)
        : 0

  const rewardLeftStyle =
    trackWidth > 0 && layout.positions.length > 0
      ? { left: rewardViewportLeft }
      : rewardIndex >= 0
        ? { left: `${Math.min(100, (targets[rewardIndex] / max) * 100)}%` }
        : undefined

  const markerClipPad = 4

  return (
    <div
      className={clsx(
        'relative w-full overflow-visible',
        checkpointLaneHeights[size],
        showCheckpointReward && checkpointRewardOffsets[size],
        className
      )}
    >
      <div
        ref={trackRef}
        role="progressbar"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuetext={ariaValueText}
        className={clsx(
          'absolute left-0 right-0 top-1/2 z-0 -translate-y-1/2 overflow-hidden rounded',
          'bg-[#ececf2] shadow-[inset_0_2px_4px_rgba(0,0,0,0.06)]',
          segmentHeights[size]
        )}
      >
        {showFullSuccess && progressPercent > 0 && (
          <div
            data-fill="success"
            className={clsx(
              'h-full rounded-[inherit] transition-[width] duration-[600ms] ease-[cubic-bezier(0.22,1,0.36,1)]',
              segmentFills.success
            )}
            style={{ width: `${progressPercent}%` }}
          />
        )}

        {!showFullSuccess && achievedPercent > 0 && (
          <div
            data-fill="achieved"
            className={clsx(
              'absolute inset-y-0 left-0 rounded-[inherit] transition-[width] duration-[600ms] ease-[cubic-bezier(0.22,1,0.36,1)]',
              segmentFills.success
            )}
            style={{ width: `${achievedPercent}%` }}
          />
        )}

        {!showFullSuccess && progressPercent > achievedPercent && (
          <div
            data-fill="progress"
            className={clsx(
              'absolute inset-y-0 rounded-[inherit] transition-[width] duration-[600ms] ease-[cubic-bezier(0.22,1,0.36,1)]',
              segmentFills[variant]
            )}
            style={{
              left: `${achievedPercent}%`,
              width: `${progressPercent - achievedPercent}%`
            }}
          />
        )}

        {!showFullSuccess && achievedPercent === 0 && progressPercent > 0 && (
          <div
            data-fill="progress"
            className={clsx(
              'h-full rounded-[inherit] transition-[width] duration-[600ms] ease-[cubic-bezier(0.22,1,0.36,1)]',
              segmentFills[variant]
            )}
            style={{ width: `${progressPercent}%` }}
          />
        )}
      </div>

      <div
        className="pointer-events-none absolute left-0 right-0 top-1/2 z-10 -translate-y-1/2"
        style={{
          height: markerSizePx[size],
          clipPath: `inset(-${markerClipPad}px 0 -${markerClipPad}px 0)`,
          overflow: 'hidden'
        }}
        aria-hidden
        data-checkpoint-panned={layout.usesPannedLayout ? 'true' : 'false'}
      >
        <div
          className={clsx(
            'relative h-full w-full',
            layout.usesPannedLayout &&
              'transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]'
          )}
          style={
            layout.usesPannedLayout
              ? { transform: `translateX(${layout.translateX}px)` }
              : undefined
          }
        >
          {targets.map((target, index) => {
            const usePixelLayout =
              trackWidth > 0 && layout.positions.length > 0

            return (
              <div
                key={`checkpoint-${index}`}
                className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2"
                style={
                  usePixelLayout
                    ? { left: layout.positions[index] }
                    : { left: `${Math.min(100, (target / max) * 100)}%` }
                }
              >
                <div className="relative flex items-center justify-center">
                  <SegmentCheckpoint
                    state={checkpointStates[index]}
                    variant={variant}
                    size={size}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {rewardIndex >= 0 && rewardLeftStyle && checkpointReward != null && (
        <div
          className="pointer-events-none absolute top-1/2 z-20 -translate-x-1/2 -translate-y-1/2"
          style={rewardLeftStyle}
        >
          <div className="relative flex items-center justify-center">
            <span className={clsx(markerSizes[size], 'opacity-0')} aria-hidden />
            <div className="absolute left-1/2 top-[calc(100%-2px)] -translate-x-1/2">
              <CheckpointRewardFloater value={checkpointReward} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Barra segmentada — a representação da escada de marcos.
 *
 * Modo legado: um segmento por marco, sem rótulo numérico.
 *
 * Modo contínuo (`showMarkers` + `max` + alvos): trilha única com checkpoints
 * proporcionais e recompensa flutuante no próximo marco.
 *
 * @example
 * <ProgressBar.Segments total={15} achieved={1} currentFill={0.4} variant="progress" />
 */
const Segments = ({
  total,
  achieved,
  currentFill = 0,
  progressValue = 0,
  max = 0,
  checkpointTargets = [],
  showMarkers = false,
  checkpointReward,
  variant = 'primary',
  size = 'sm',
  className,
  'aria-valuetext': ariaValueText
}: ProgressBarSegmentsProps) => {
  const segmentCount = Math.max(0, Math.floor(total))
  const targets = resolveCheckpointTargets(checkpointTargets, segmentCount, max)
  const useContinuous = showMarkers && max > 0 && targets.length > 0

  if (useContinuous) {
    const value = Math.max(0, progressValue)
    const fillPercent = Math.min(100, (value / max) * 100)
    const firstPendingIndex = targets.findIndex(target => value < target)

    const checkpointStates: SegmentState[] = targets.map((target, index) => {
      if (value >= target) return 'achieved'
      if (index === firstPendingIndex) return 'current'
      return 'pending'
    })

    const lastAchievedTarget = lastAchievedCheckpointTarget(value, targets)
    const achievedPercent = Math.min(100, (lastAchievedTarget / max) * 100)
    const progressPercent = fillPercent
    const showFullSuccess = variant === 'success' || value >= max
    const showCheckpointReward =
      checkpointReward != null && checkpointReward > 0 && firstPendingIndex >= 0

    return (
      <ContinuousCheckpointBar
        value={value}
        max={max}
        targets={targets}
        checkpointStates={checkpointStates}
        firstPendingIndex={firstPendingIndex}
        checkpointReward={checkpointReward}
        showCheckpointReward={showCheckpointReward}
        variant={variant}
        size={size}
        className={className}
        ariaValueText={ariaValueText}
        showFullSuccess={showFullSuccess}
        achievedPercent={achievedPercent}
        progressPercent={progressPercent}
      />
    )
  }

  const achievedCount = Math.min(Math.max(0, Math.floor(achieved)), segmentCount)
  const fill = Math.min(1, Math.max(0, currentFill))

  return (
    <div
      className={clsx('flex w-full gap-[2px]', className)}
      role="progressbar"
      aria-valuenow={achievedCount}
      aria-valuemin={0}
      aria-valuemax={segmentCount}
      aria-valuetext={ariaValueText}
    >
      {Array.from({ length: segmentCount }, (_, index) => {
        const isAchieved = index < achievedCount
        const isCurrent = index === achievedCount

        return (
          <div
            key={index}
            data-state={isAchieved ? 'achieved' : isCurrent ? 'current' : 'pending'}
            className={clsx(
              'min-w-0 flex-1 overflow-hidden rounded-[2px] bg-[#ececf2]',
              segmentHeights[size]
            )}
          >
            {(isAchieved || (isCurrent && fill > 0)) && (
              <div
                className={clsx(
                  'h-full rounded-[inherit] transition-[width] duration-[600ms] ease-[cubic-bezier(0.22,1,0.36,1)]',
                  segmentFills[variant]
                )}
                style={{ width: isAchieved ? '100%' : `${fill * 100}%` }}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}
Segments.displayName = 'ProgressBar.Segments'

ProgressBar.Segments = Segments

export type ProgressBarSegmentsRowProps = ProgressBarSegmentsProps & {
  /** Linha esquerda — ex.: "7 de 75 objetivos". */
  label: ReactNode
  /** Percentual exibido à direita; calculado de progressValue/max se omitido. */
  percent?: number
}

/**
 * Escada de marcos no layout canônico de duas linhas: objetivos à esquerda,
 * percentual à direita, barra contínua com checkpoints nos marcos.
 */
const SegmentsRow = ({
  label,
  percent,
  total,
  achieved,
  currentFill = 0,
  progressValue = 0,
  max = 0,
  checkpointTargets = [],
  showMarkers = true,
  checkpointReward,
  variant,
  size,
  className,
  'aria-valuetext': ariaValueText
}: ProgressBarSegmentsRowProps) => {
  const segmentCount = Math.max(0, Math.floor(total))
  const achievedCount = Math.min(Math.max(0, Math.floor(achieved)), segmentCount)
  const fill = Math.min(1, Math.max(0, currentFill))

  const milestonePercent =
    segmentCount > 0
      ? Math.min(100, Math.round(((achievedCount + fill) / segmentCount) * 100))
      : 0

  const objectivePercent =
    max > 0
      ? Math.min(100, Math.round((Math.max(0, progressValue) / max) * 100))
      : milestonePercent

  const displayPercent = percent ?? objectivePercent

  return (
    <div className={clsx('flex flex-col gap-1.5', className)}>
      <div className="flex items-center justify-between gap-2">
        <span className="min-w-0 text-2xs font-semibold text-gray-600">{label}</span>
        <span className="shrink-0 text-sm font-bold text-gray-800">{displayPercent}%</span>
      </div>
      <Segments
        total={total}
        achieved={achieved}
        currentFill={currentFill}
        progressValue={progressValue}
        max={max}
        checkpointTargets={checkpointTargets}
        showMarkers={showMarkers}
        checkpointReward={checkpointReward}
        variant={variant}
        size={size}
        aria-valuetext={ariaValueText}
      />
    </div>
  )
}
SegmentsRow.displayName = 'ProgressBar.SegmentsRow'

ProgressBar.SegmentsRow = SegmentsRow
